import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { 
  FiHome, FiBookOpen, FiUsers, FiDollarSign, FiSettings, 
  FiLogOut, FiMenu, FiBell 
} from 'react-icons/fi';

const InstructorLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { icon: FiHome, label: 'لوحة التحكم', path: '/instructor/dashboard' },
    { icon: FiBookOpen, label: 'كورساتي', path: '/instructor/courses' },
    { icon: FiUsers, label: 'الطلاب', path: '/instructor/students' },
    { icon: FiDollarSign, label: 'المحفظة والأرباح', path: '/instructor/wallet' },
    { icon: FiSettings, label: 'الإعدادات', path: '/instructor/settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans text-right" dir="rtl">
      
      {/* 1. SIDEBAR (Indigo Theme) */}
      <aside 
        className={`bg-indigo-900 text-white fixed lg:static inset-y-0 right-0 z-40 w-64 transform transition-transform duration-300 ease-in-out shadow-xl ${
          isSidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0 lg:w-20'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo Area */}
          <div className="h-16 flex items-center justify-center border-b border-indigo-800 bg-indigo-950/30">
             {isSidebarOpen ? (
               <h1 className="text-xl font-bold tracking-wider">Instructor <span className="text-indigo-300 text-xs">PANEL</span></h1>
             ) : (
               <span className="font-bold text-xl">iT</span>
             )}
          </div>

          {/* User Mini Profile (Sidebar) */}
          {isSidebarOpen && (
            <div className="p-4 border-b border-indigo-800 flex items-center gap-3">
               <img 
                 src={user?.photoURL || `https://ui-avatars.com/api/?name=${user?.firstName}&background=6366f1&color=fff`} 
                 className="w-10 h-10 rounded-full border-2 border-indigo-400"
                 alt=""
               />
               <div className="overflow-hidden">
                 <p className="font-bold text-sm truncate">{user?.firstName} {user?.lastName}</p>
                 <p className="text-xs text-indigo-300 truncate">مدرب معتمد</p>
               </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 py-6 space-y-2 overflow-y-auto px-2">
            {navItems.map((item) => {
              const isActive = location.pathname.includes(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                    isActive 
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/50' 
                      : 'text-indigo-200 hover:bg-indigo-800 hover:text-white'
                  }`}
                  title={item.label}
                >
                  <item.icon size={20} className={`${isActive ? 'text-white' : 'text-indigo-300 group-hover:text-white'}`} />
                  <span className={`font-medium whitespace-nowrap transition-all duration-200 ${!isSidebarOpen && 'hidden lg:hidden'}`}>
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-indigo-800">
            <button 
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-300 hover:bg-indigo-800 hover:text-red-200 transition-colors"
            >
              <FiLogOut size={20} />
              <span className={`${!isSidebarOpen && 'hidden lg:hidden'}`}>تسجيل خروج</span>
            </button>
          </div>
        </div>
      </aside>

      {/* 2. MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        
        {/* Top Header */}
        <header className="bg-white shadow-sm h-16 flex items-center justify-between px-6 z-30">
          <button 
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 focus:outline-none"
          >
            <FiMenu size={24} />
          </button>
          
          <div className="flex items-center gap-4">
             <button className="relative p-2 text-gray-400 hover:text-indigo-600 transition-colors">
               <FiBell size={20} />
               <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
             </button>
             <div className="h-8 w-[1px] bg-gray-200 mx-2"></div>
             <div className="text-sm text-gray-500">
               <span className="hidden md:inline">مرحباً، </span>
               <span className="font-bold text-gray-900">{user?.firstName}</span>
             </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
           <Outlet /> 
        </main>

      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default InstructorLayout;